import { DrawerCollapsible } from "components/UI/DrawerCollapsible/DrawerCollapsible.tsx";
import { DrawerContent } from "components/UI/DrawerContent/DrawerContent.tsx";
import { FormCol } from "components/UI/Form/FormCol.tsx";
import { FormGrid } from "components/UI/Form/FormGrid.tsx";
import { TagSelect } from "components/UI/TagSelect/TagSelect.tsx";
import { TextArea } from "components/UI/TextArea/TextArea.tsx";
import { usePaldockTranslation } from "hooks/usePaldockTranslation.tsx";
import { useUser } from "hooks/useUser.ts";
import { FC, useCallback, useMemo, useState } from "react";

type ConversionType = "initiation" | "lead" | "prospect" | "sale" | "custom";

const PALDOCK_CONVERSION_BOOTSTRAP =
    '(function(w,p){p=w.paldock=w.paldock||{};p.q=p.q||[];p.conversion=p.conversion||function(){p.q.push(["conversion"].concat([].slice.call(arguments)))}})(window);';

interface ConversionPixelFormProps {
    conversionType: ConversionType;
}

export const ConversionPixelForm: FC<ConversionPixelFormProps> = ({ conversionType }) => {
    const { t } = usePaldockTranslation("forms.conversion-pixel");
    const userUtils = useUser();
    const tenantSlug = userUtils.getActiveTenant()?.slug_name ?? "YOUR_TENANT_ID";
    const [selectedTags, setSelectedTags] = useState<string[]>([]);

    const isInitiation = conversionType === "initiation";

    const initiationTagsOptions = useMemo(
        () => [
            { label: "expiration", value: "expiration" },
            { label: "pcidParam", value: "pcidParam" },
        ],
        []
    );

    const createTagsOptions = useMemo(
        () => [
            { label: "commission_id", value: "commission_id" },
            { label: "external_id", value: "external_id" },
            { label: "value", value: "value" },
            { label: "result", value: "result" },
            { label: "commission", value: "commission" },
            { label: "affcid", value: "affcid" },
            { label: "affs1", value: "affs1" },
            { label: "affs2", value: "affs2" },
            { label: "affs3", value: "affs3" },
            { label: "advs1", value: "advs1" },
            { label: "advs2", value: "advs2" },
            { label: "advs3", value: "advs3" },
            { label: "queryParam", value: "queryParam" },
            { label: "cookieParam", value: "cookieParam" },
        ],
        []
    );

    const updateTagsOptions = useMemo(
        () => [
            { label: "external_id", value: "external_id" },
            { label: "value", value: "value" },
            { label: "result", value: "result" },
            { label: "commission", value: "commission" },
            { label: "affcid", value: "affcid" },
            { label: "affs1", value: "affs1" },
            { label: "affs2", value: "affs2" },
            { label: "affs3", value: "affs3" },
            { label: "advs1", value: "advs1" },
            { label: "advs2", value: "advs2" },
            { label: "advs3", value: "advs3" },
            { label: "queryParam", value: "queryParam" },
            { label: "cookieParam", value: "cookieParam" },
        ],
        []
    );

    const conversionTagsOptions = conversionType === "lead" ? updateTagsOptions : createTagsOptions;

    const tagsOptions = isInitiation ? initiationTagsOptions : conversionTagsOptions;

    const getActionForType = (type: ConversionType): string | null => {
        switch (type) {
            case "initiation":
                return null;
            case "lead":
                return "update";
            default:
                return "create";
        }
    };

    const buildPixelCode = useCallback(
        (type: ConversionType, tags: string[]) => {
            // Initiation only loads the script
            if (type === "initiation") {
                const pixelCdnUrl = import.meta.env.VITE_PIXEL_CDN_URL;
                const pixelVersion = import.meta.env.VITE_PIXEL_VERSION;
                const baseUrl = `${pixelCdnUrl}/paldock-pixel-${pixelVersion}.js?t=${tenantSlug}&cookie=1`;
                const params = tags.map((tag) => `${tag}=REPLACE_ME`);
                const fullUrl = params.length > 0 ? `${baseUrl}&${params.join("&")}` : baseUrl;

                return `<!-- pixel load -->
<script async src="${fullUrl}"></script>`;
            }

            // Other types have paldock initialization + conversion call
            const action = getActionForType(type);

            const lines: string[] = [];
            lines.push(`      action: '${action}',`);
            lines.push(`      type: '${type}',`);
            lines.push(`      advertiser_id: 'REPLACE_ME',`);

            // Add optional fields from tags
            tags.forEach((tag) => {
                if (tag === "queryParam") {
                    lines.push(`      queryParam: {`);
                    lines.push(`          REPLACE_KEY: 'REPLACE_ME',`);
                    lines.push(`      },`);
                } else if (tag === "cookieParam") {
                    lines.push(`      cookieParam: {`);
                    lines.push(`          REPLACE_KEY: 'REPLACE_ME',`);
                    lines.push(`      },`);
                } else {
                    lines.push(`      ${tag}: 'REPLACE_ME',`);
                }
            });

            return `<script>
  ${PALDOCK_CONVERSION_BOOTSTRAP}

  paldock.conversion({
${lines.join("\n")}
  });
</script>`;
        },
        [tenantSlug]
    );

    const pixelCode = useMemo(
        () => buildPixelCode(conversionType, selectedTags),
        [buildPixelCode, conversionType, selectedTags]
    );

    const handleTagsChange = (tags: string[]) => {
        setSelectedTags(tags);
    };

    return (
        <DrawerContent>
            <DrawerCollapsible title={t("sections.settings")}>
                <FormCol>
                    <FormGrid cols={1}>
                        <TextArea value={pixelCode} appearance="readonly" readOnly rows={isInitiation ? 3 : 10} />
                        <TagSelect options={tagsOptions} value={selectedTags} onChange={handleTagsChange} />
                    </FormGrid>
                </FormCol>
            </DrawerCollapsible>
        </DrawerContent>
    );
};
